/**
 * GalleryField — Phase 19-C
 *
 * Manages a list of up to 6 product gallery images. Each slot stores
 * { url, alt?, title?, sort_order } and is rendered as:
 *   [thumbnail] [alt input] [title input] [↑ ↓] [×]
 *
 * Hergebruik:
 *  - <MediaPickerDialog>      → kies bestaande media
 *  - POST /admin/media/upload → upload vanaf computer (zelfde RBAC en
 *                               validatie als ImageField in Phase 19-B)
 *
 * R4: alle sub-componenten zijn top-level — geen focus-loss tijdens
 * typen in alt/title inputs.
 *
 * Props:
 *   value     Array<{url, alt?, title?, sort_order?} | string>
 *             (string items uit legacy producten worden tolereerd; bij save
 *              wordt de server-validator ze normaliseren naar objecten)
 *   onChange  (newValue: Array) => void   — caller persisteert in editing
 *   max       number = 6
 *   testIdPrefix string default "gallery-field"
 */
import React, { useRef, useState } from "react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import {
  Upload, FolderOpen, X, ArrowUp, ArrowDown, ImageIcon, Loader2, Plus,
} from "lucide-react";
import MediaPickerDialog from "./MediaPickerDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT_MIME = "image/png,image/jpeg,image/jpg,image/webp";
const ALLOWED_MIME_SET = new Set([
  "image/png", "image/jpeg", "image/jpg", "image/webp",
]);

function previewSrc(url) {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

// Normalise legacy strings to objects so the UI always renders identically.
// Server-side validator does the same; this just keeps the form in sync.
function normalise(items) {
  if (!Array.isArray(items)) return [];
  return items.map((it, i) => {
    if (typeof it === "string") return { url: it, sort_order: i };
    return { ...it, sort_order: it?.sort_order ?? i };
  });
}

// ---------- Sub-component (top-level for R4 stability) ----------
function GallerySlot({
  item, idx, total, onUpdate, onRemove, onMove, busy,
}) {
  return (
    <div
      className="flex flex-col sm:flex-row gap-3 p-3 bg-white border border-gray-200"
      data-testid={`gallery-slot-${idx}`}
    >
      {/* Thumbnail */}
      <div className="w-20 h-20 flex-shrink-0 border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
        {item?.url ? (
          <img
            src={previewSrc(item.url)}
            alt={item.alt || `Gallery ${idx + 1}`}
            className="w-full h-full object-contain"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            data-testid={`gallery-slot-thumb-${idx}`}
          />
        ) : (
          <ImageIcon className="w-6 h-6 text-gray-300" />
        )}
      </div>
      {/* Metadata inputs */}
      <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <Label className="text-[10px] uppercase font-bold tracking-wide">Alt (SEO)</Label>
          <Input
            value={item?.alt || ""}
            onChange={(e) => onUpdate(idx, { ...item, alt: e.target.value })}
            placeholder={`Bijv. shirt foto ${idx + 1}`}
            className="rounded-none mt-1 text-sm"
            data-testid={`gallery-slot-alt-${idx}`}
          />
        </div>
        <div>
          <Label className="text-[10px] uppercase font-bold tracking-wide">Titel</Label>
          <Input
            value={item?.title || ""}
            onChange={(e) => onUpdate(idx, { ...item, title: e.target.value })}
            placeholder="Optioneel"
            className="rounded-none mt-1 text-sm"
            data-testid={`gallery-slot-title-${idx}`}
          />
        </div>
      </div>
      {/* Actions */}
      <div className="flex sm:flex-col gap-1 items-end sm:items-center justify-end">
        <button
          type="button"
          onClick={() => onMove(idx, "up")}
          disabled={busy || idx === 0}
          className="text-gray-500 hover:text-[#0055FF] disabled:text-gray-300 disabled:cursor-not-allowed p-1"
          title="Omhoog"
          data-testid={`gallery-slot-up-${idx}`}
        >
          <ArrowUp className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onMove(idx, "down")}
          disabled={busy || idx === total - 1}
          className="text-gray-500 hover:text-[#0055FF] disabled:text-gray-300 disabled:cursor-not-allowed p-1"
          title="Omlaag"
          data-testid={`gallery-slot-down-${idx}`}
        >
          <ArrowDown className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onRemove(idx)}
          disabled={busy}
          className="text-red-500 hover:text-red-700 disabled:opacity-50 p-1"
          title="Verwijderen"
          data-testid={`gallery-slot-remove-${idx}`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ---------- Main ----------
export default function GalleryField({
  value,
  onChange,
  max = 6,
  testIdPrefix = "gallery-field",
}) {
  const items = normalise(value);
  const atLimit = items.length >= max;
  const fileRef = useRef(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const addItem = (newItem) => {
    if (atLimit) {
      toast.error(`Maximaal ${max} gallery-afbeeldingen per product.`);
      return;
    }
    onChange?.([...items, { ...newItem, sort_order: items.length }]);
  };

  const updateItem = (idx, next) => {
    const copy = items.map((it, i) => (i === idx ? next : it));
    onChange?.(copy);
  };

  const removeItem = (idx) => {
    const copy = items.filter((_, i) => i !== idx)
      .map((it, i) => ({ ...it, sort_order: i }));
    onChange?.(copy);
    toast.message("Gallery-afbeelding verwijderd");
  };

  const moveItem = (idx, direction) => {
    const swap = direction === "up" ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= items.length) return;
    const copy = [...items];
    [copy[idx], copy[swap]] = [copy[swap], copy[idx]];
    onChange?.(copy.map((it, i) => ({ ...it, sort_order: i })));
  };

  const triggerFile = () => {
    if (atLimit) {
      toast.error(`Maximaal ${max} gallery-afbeeldingen per product.`);
      return;
    }
    if (!uploading && fileRef.current) fileRef.current.click();
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("Bestand is te groot. Maximaal 5 MB.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    if (!ALLOWED_MIME_SET.has(file.type)) {
      toast.error("Alleen JPG, PNG en WEBP zijn toegestaan.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("alt_text", "");
    fd.append("title", "");
    setUploading(true);
    try {
      const r = await adminApi.post("/admin/media/upload", fd, {
        headers: { "Content-Type": undefined },
      });
      const url = r.data?.url || `/api/media/${r.data?.id}`;
      addItem({ url, alt: r.data?.alt_text || "", title: r.data?.title || "" });
      toast.success("Gallery-afbeelding toegevoegd");
    } catch (err) {
      toast.error(formatApiError(err, "Afbeelding kon niet worden geüpload."));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onPickerSelect = (selectedItem) => {
    const url = selectedItem?.url || `/api/media/${selectedItem?.id}`;
    addItem({
      url,
      alt: selectedItem?.alt_text || "",
      title: selectedItem?.title || "",
    });
    toast.success("Gallery-afbeelding gekoppeld uit Media Library");
  };

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-root`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <Label className="text-xs uppercase font-bold">
            Gallery-afbeeldingen{" "}
            <span className="text-gray-500 font-normal">
              ({items.length} / {max})
            </span>
          </Label>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Extra foto&apos;s zoals achterkant, detailprint of close-up
            stof. Klant kan op de thumbnails klikken op de productkaart.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT_MIME}
            onChange={onFile}
            className="hidden"
            data-testid={`${testIdPrefix}-file-input`}
          />
          <Button
            type="button"
            onClick={triggerFile}
            disabled={uploading || atLimit}
            className="rounded-none bg-[#0055FF] hover:bg-[#0A192F] text-white font-chivo font-bold uppercase text-xs"
            data-testid={`${testIdPrefix}-upload-button`}
          >
            {uploading
              ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              : <Upload className="w-3.5 h-3.5 mr-1" />}
            {uploading ? "Uploaden…" : "Uploaden"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => atLimit
              ? toast.error(`Maximaal ${max} gallery-afbeeldingen per product.`)
              : setPickerOpen(true)}
            disabled={atLimit}
            className="rounded-none font-chivo font-bold uppercase text-xs"
            data-testid={`${testIdPrefix}-library-button`}
          >
            <FolderOpen className="w-3.5 h-3.5 mr-1" /> Kies uit Media
          </Button>
        </div>
      </div>

      {atLimit && (
        <div
          className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2"
          data-testid={`${testIdPrefix}-limit-message`}
        >
          Limiet bereikt: maximaal {max} gallery-afbeeldingen per product. Verwijder eerst een afbeelding om een nieuwe toe te voegen.
        </div>
      )}

      {items.length === 0 ? (
        <div
          className="text-xs text-gray-500 bg-gray-50 border border-dashed border-gray-300 px-4 py-6 text-center"
          data-testid={`${testIdPrefix}-empty`}
        >
          <Plus className="w-4 h-4 inline mr-1" />
          Nog geen gallery-afbeeldingen. Voeg er één toe via <strong>Uploaden</strong> of <strong>Kies uit Media</strong>.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it, idx) => (
            <GallerySlot
              key={`${it.url}-${idx}`}
              item={it}
              idx={idx}
              total={items.length}
              onUpdate={updateItem}
              onRemove={removeItem}
              onMove={moveItem}
              busy={uploading}
            />
          ))}
        </div>
      )}

      <MediaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={onPickerSelect}
      />
    </div>
  );
}
